import { useMemo } from 'react';
import type { ProductoDto } from '@/types/producto';
import type { PantallaModularDto } from '@/types/pantallaModular';

interface LineItem {
  tipo: 'GABINETE' | 'MODULAR';
  producto: ProductoDto | null;
  pantallaModular: PantallaModularDto | null;
  columnas: number;
  filas: number;
  rotado: boolean;
  precioM2: number;
  cantidad: number;
}

interface CalculationParams {
  lineItems: LineItem[];
  descuentoActivo: boolean;
  porcentajeDescuento: number;
  costoEnvio: number;
  precioControlador?: number;
  adicionales?: { descripcion: string; precio: number; cantidad: number }[];
}

export function useQuotationCalculations(params: CalculationParams) {
  return useMemo(() => {
    const itemCalculations = params.lineItems.map((item) => {
      const precioM2 = item.precioM2 || 0;
      const cantidadPantallas = Math.max(1, item.cantidad || 1);

      if (item.tipo === 'MODULAR' && item.pantallaModular) {
        const pm = item.pantallaModular;
        const cantidadModulos = item.columnas * item.filas;
        const efectivoAnchoMm = item.rotado ? pm.altoModuloMm : pm.anchoModuloMm;
        const efectivoAltoMm  = item.rotado ? pm.anchoModuloMm : pm.altoModuloMm;
        const anchoTotalM = (item.columnas * efectivoAnchoMm) / 1000;
        const altoTotalM = (item.filas * efectivoAltoMm) / 1000;
        const areaM2 = anchoTotalM * altoTotalM;
        const areaModulo = (pm.anchoModuloMm * pm.altoModuloMm) / 1_000_000;
        const precioUnitario = Math.round(areaModulo * precioM2 * 100) / 100;
        const subtotalProducto = Math.round(precioUnitario * cantidadModulos * 100) / 100;
        const subtotalPantalla = Math.round(subtotalProducto * cantidadPantallas * 100) / 100;
        return {
          cantidadGabinetes: cantidadModulos,
          anchoTotalM,
          altoTotalM,
          areaM2,
          precioUnitario,
          subtotalProducto,
          subtotalPantalla,
        };
      }

      if (!item.producto) {
        return {
          cantidadGabinetes: 0,
          anchoTotalM: 0,
          altoTotalM: 0,
          areaM2: 0,
          precioUnitario: 0,
          subtotalProducto: 0,
          subtotalPantalla: 0,
        };
      }

      const cantidadGabinetes = item.columnas * item.filas;
      const anchoTotalM = (item.columnas * item.producto.anchoCm) / 100;
      const altoTotalM = (item.filas * item.producto.altoCm) / 100;
      const areaM2 = anchoTotalM * altoTotalM;

      const areaCabinet = (item.producto.anchoCm * item.producto.altoCm) / 10000;
      // Si el gabinete es >= 0.9 m² (ej. P10 96×96cm), el precio es por gabinete directamente
      const precioUnitario = areaCabinet >= 0.9
        ? Math.round(precioM2 * 100) / 100
        : Math.round(areaCabinet * precioM2 * 100) / 100;
      const subtotalProducto = Math.round(precioUnitario * cantidadGabinetes * 100) / 100;
      const subtotalPantalla = Math.round(subtotalProducto * cantidadPantallas * 100) / 100;

      return {
        cantidadGabinetes,
        anchoTotalM,
        altoTotalM,
        areaM2,
        precioUnitario,
        subtotalProducto,
        subtotalPantalla,
      };
    });

    const subtotal = itemCalculations.reduce((sum, item) => sum + item.subtotalPantalla, 0);
    const precioControlador = params.precioControlador || 0;
    const sumaAdicionales = (params.adicionales || []).reduce((sum, a) => sum + (a.precio || 0) * (a.cantidad || 1), 0);
    const baseParaDescuento = subtotal + precioControlador + sumaAdicionales;

    let descuento = 0;
    if (params.descuentoActivo && params.porcentajeDescuento > 0) {
      descuento = Math.round(baseParaDescuento * params.porcentajeDescuento) / 100;
    }

    const totalFinal = Math.round((baseParaDescuento - descuento + params.costoEnvio) * 100) / 100;

    return {
      itemCalculations,
      subtotal,
      precioControlador,
      sumaAdicionales,
      descuento,
      totalFinal,
    };
  }, [params]);
}
